import { FC, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import Customer from "../../models/Customer";
import { HiCheck, HiOutlineClock } from "react-icons/hi";
import { Timestamp } from "../../types";
import { OrderStatus } from "../../types/order/IOrder";
import { Order } from "../../models";
import {
  TabButton,
  TableSkeletonsByAmount,
  noCompletedOrders,
  noUpcomingOrders,
  Button,
} from "@components";

interface RelatedOrdersProps {
  orders: Order[];
  loading?: boolean;
  driverId?: string;
}

export const RelatedOrders: FC<RelatedOrdersProps> = ({
  orders,
  loading,
  driverId,
}) => {
  const { t } = useTranslation(["common", "customer"]);
  const location = useLocation();

  // UI State
  const [hasLoadedMoreUpcoming, setHasLoadedMoreUpcoming] = useState(false);
  const [hasLoadedMoreCompleted, setHasLoadedMoreCompleted] = useState(false);

  const [activeTab, setActiveTab] = useState<"completed" | "upcoming">(
    "completed",
  );

  const ordersPerPage = 5;

  const formatTimestamp = (
    timestamp: Timestamp | string | undefined,
  ): string => {
    if (!timestamp) return t("common:unknown_date");
    if (typeof timestamp === "string") return timestamp;
    return new Date(timestamp.seconds * 1000).toLocaleDateString();
  };

  const currentPath = location.pathname + location.search;

  const getOrderUrl = (orderId: string) => {
    const params = new URLSearchParams();
    params.set("id", orderId);

    if (driverId) {
      params.set("driver", driverId);
    }

    return `/orders?${params.toString()}`;
  };

  // Beregn ordre-data direkte med useMemo - ingen useEffect
  const ordersData = useMemo(() => {
    if (!orders)
      return {
        completed: [],
        upcoming: [],
        completedCount: 0,
        upcomingCount: 0,
      };

    const completed = orders.filter(
      (order) => order.status === OrderStatus.Completed,
    );
    const upcoming = orders.filter(
      (order) =>
        order.status === OrderStatus.New ||
        order.status === OrderStatus.Scheduled ||
        order.status === OrderStatus.InProgress,
    );

    const completedOrdersList = completed.map((order) => ({
      title: Customer.getCustomerName(order.customer as Customer),
      value: `From ${
        order.route[0]?.location?.addressLine || t("customer:unknown_address")
      } to ${
        order.route[order.route.length - 1]?.location?.addressLine ||
        t("customer:unknown_address")
      }`,
      stopDate: order.route[order.route.length - 1]?.stopDate || null,
      orderId: order.id,
    }));

    const upcomingOrdersList = upcoming.map((order) => ({
      title: Customer.getCustomerName(order.customer as Customer),
      value: `From ${
        order.route[0]?.location?.addressLine || t("customer:unknown_address")
      } to ${
        order.route[order.route.length - 1]?.location?.addressLine ||
        t("customer:unknown_address")
      }`,
      stopDate: order.route[order.route.length - 1]?.stopDate || null,
      orderId: order.id,
    }));

    completedOrdersList.sort(
      (a, b) => (b.stopDate?.seconds || 0) - (a.stopDate?.seconds || 0),
    );
    upcomingOrdersList.sort(
      (a, b) => (b.stopDate?.seconds || 0) - (a.stopDate?.seconds || 0),
    );

    return {
      completed: completedOrdersList,
      upcoming: upcomingOrdersList,
      completedCount: completedOrdersList.length,
      upcomingCount: upcomingOrdersList.length,
    };
  }, [orders, t]);

  const {
    completed: completedOrders,
    upcoming: upcomingOrders,
    completedCount: allCompletedOrdersCount,
    upcomingCount: allUpcomingOrdersCount,
  } = ordersData;

  // Forenklet paginering - bruk direkte data fra useMemo
  const loadMoreOrders = (type: "completed" | "upcoming") => {
    if (type === "completed") {
      setHasLoadedMoreCompleted(true);
    } else {
      setHasLoadedMoreUpcoming(true);
    }
  };

  return (
    <>
      <div className="mt-6">
        <p className="text-xl font-medium dark:text-text mb-4">
          {t("customer:related_orders")}
        </p>

        <div className="mb-4 border-b border-border">
          <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
            <TabButton
              label={t("customer:completed_orders")}
              isSelected={activeTab === "completed"}
              onClick={() => setActiveTab("completed")}
            />
            <TabButton
              label={t("customer:upcoming_orders")}
              isSelected={activeTab === "upcoming"}
              onClick={() => setActiveTab("upcoming")}
            />
          </ul>
        </div>

        {activeTab === "completed" ? (
          <div className="pt-4 pb-4">
            {loading ? (
              <TableSkeletonsByAmount quantity={4} />
            ) : completedOrders.length === 0 ? (
              <div className="flex flex-col items-center">
                <div className="mb-8 mt-8">{noCompletedOrders}</div>
                <p className="text-muted">
                  {t("customer:no_completed_orders")}
                </p>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-border">
                  {completedOrders
                    .slice(
                      0,
                      hasLoadedMoreCompleted
                        ? completedOrders.length
                        : ordersPerPage,
                    )
                    .map(({ title, value, stopDate, orderId }) => (
                      <li key={orderId} className="pb-3 sm:pb-4">
                        <Link
                          to={getOrderUrl(orderId)}
                          state={{ from: currentPath, driverId: driverId }}
                        >
                          <div className="w-full flex items-center justify-between bg-white shadow-custom rounded-lg p-4 dark:bg-surface hover:shadow-md transition-shadow duration-200 cursor-pointer border border-lgb-grey-100 dark:border-border-subtle">
                            <div className="flex items-center space-x-4 rtl:space-x-reverse max-w-[70%]">
                              <div className="flex-shrink-0">
                                <HiCheck className="h-6 w-6 text-green-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate dark:text-white">
                                  {title || t("customer:unknown_customer")}
                                </p>
                                <p className="text-sm text-gray-500 line-clamp-2 dark:text-muted">
                                  {value}
                                </p>
                              </div>
                            </div>
                            <div className="text-sm text-gray-500 text-right min-w-[80px]">
                              {stopDate
                                ? formatTimestamp(stopDate)
                                : t("customer:unknown_date")}
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                </ul>

                {ordersPerPage < allCompletedOrdersCount &&
                  !hasLoadedMoreCompleted && (
                    <Button
                      className="mt-4 bg-white border-lgb-grey-600 text-secondary hover:bg-surface-muted"
                      onClick={() => {
                        loadMoreOrders("completed");
                        setHasLoadedMoreCompleted(true);
                      }}
                    >
                      {t("customer:load_more_completed")}
                    </Button>
                  )}
                {hasLoadedMoreCompleted && (
                  <Button
                    className="mt-2 bg-white border-lgb-grey-600 text-secondary hover:bg-surface-muted"
                    onClick={() => {
                      setHasLoadedMoreCompleted(false);
                    }}
                  >
                    {t("customer:show_less")}
                  </Button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="pt-4 pb-4">
            {loading ? (
              <TableSkeletonsByAmount quantity={4} />
            ) : upcomingOrders.length === 0 ? (
              <div className="flex flex-col items-center">
                <div className="mb-8 mt-8">{noUpcomingOrders}</div>
                <p className="text-muted">
                  {t("customer:no_upcoming_orders")}
                </p>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-border">
                  {upcomingOrders
                    .slice(
                      0,
                      hasLoadedMoreUpcoming
                        ? upcomingOrders.length
                        : ordersPerPage,
                    )
                    .map(({ title, value, stopDate, orderId }) => (
                      <li key={orderId} className="pb-3 sm:pb-4">
                        <Link
                          to={getOrderUrl(orderId)}
                          state={{ from: currentPath, driverId: driverId }}
                        >
                          <div className="w-full flex items-center justify-between bg-white shadow-custom rounded-lg p-4 dark:bg-surface hover:shadow-md transition-shadow duration-200 cursor-pointer border border-lgb-grey-100 dark:border-border-subtle">
                            <div className="flex items-center space-x-4 rtl:space-x-reverse max-w-[70%]">
                              <div className="flex-shrink-0">
                                <HiOutlineClock className="h-6 w-6 text-yellow-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate dark:text-white">
                                  {title || t("customer:unknown_customer")}
                                </p>
                                <p className="text-sm text-gray-500 line-clamp-2 dark:text-muted">
                                  {value}
                                </p>
                              </div>
                            </div>
                            <div className="text-sm text-gray-500 text-right min-w-[80px]">
                              {stopDate
                                ? formatTimestamp(stopDate)
                                : t("customer:unknown_date")}
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                </ul>

                {ordersPerPage < allUpcomingOrdersCount &&
                  !hasLoadedMoreUpcoming && (
                    <Button
                      className="mt-4 bg-white border-lgb-grey-600 text-secondary hover:bg-surface-muted"
                      onClick={() => {
                        loadMoreOrders("upcoming");
                        setHasLoadedMoreUpcoming(true);
                      }}
                    >
                      {t("customer:load_more_upcoming")}
                    </Button>
                  )}
                {hasLoadedMoreUpcoming && (
                  <Button
                    className="mt-2 bg-white border-lgb-grey-600 text-secondary hover:bg-surface-muted"
                    onClick={() => {
                      setHasLoadedMoreUpcoming(false);
                    }}
                  >
                    {t("customer:show_less")}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};
